/**
 * FinalStandings - end-of-set ceremony. Keeps the finale celebratory without
 * flooding the screen, and lets the host reset the room into a fresh waiting
 * state before starting another set.
 *
 * UI-4 enhancements: stagger reveal 4th→1st, MVP roll moment after the
 * winner showcase, share-card PNG to clipboard.
 */
import { useEffect, useState, useRef } from 'react';
import { feedback } from '@tt/feedback';
import { Share2 } from 'lucide-react';
import type { LobbyView, RoundXp } from '@tt/lobby';
import { IconAvatar } from '../characters/IconAvatar';
import { RoundXpSummary } from '../me/RoundXpSummary';
import { XPBar } from '../me/XPBar';

interface Props {
  lobby: LobbyView;
  finalStandings: {
    standings: Array<{ charId: string; name: string; points: number }>;
    awards: Array<{ charId: string; label: string }>;
    winnerHue: number;
  };
  isHost: boolean;
  myCharId: string;
  lastRoundXp?: RoundXp | null;
  xpRefreshKey?: number | string;
  onNewSession: () => void;
  onChangeSettings: () => void;
  onEnd: () => void;
}

export function FinalStandings({ lobby, finalStandings, isHost, myCharId, lastRoundXp, xpRefreshKey, onNewSession, onChangeSettings, onEnd }: Props) {
  useEffect(() => { feedback.cheer(); }, []);

  const { standings, awards, winnerHue } = finalStandings;
  const winner = standings[0];
  const playersById = new Map(lobby.players.map((p) => [p.charId, p]));
  const runnerUp = standings[1];
  const margin = winner && runnerUp ? Math.max(0, winner.points - runnerUp.points) : winner?.points ?? 0;

  // R2#63: when more than one player shares the winner's score (and the
  // score is non-zero), the server's awardSetWins is skipped and there
  // is no real winner — the showcase + MVP roll would otherwise label
  // the slot-order-first player as the winner, which is a lie.
  const tied = !!winner && winner.points > 0 && standings.filter((s) => s.points === winner.points).length > 1;

  // MVP — pick the player with the most named awards in `awards`. Fall
  // back to the winner if no awards exist.
  const awardCount = new Map<string, number>();
  for (const a of awards) awardCount.set(a.charId, (awardCount.get(a.charId) ?? 0) + 1);
  const mvpId = awards.length > 0
    ? [...awardCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? winner?.charId
    : winner?.charId;
  // R2#64: in a tied final, mvpId defaults to slot-order-first. Suppress
  // the MVP roll entirely rather than awarding it arbitrarily.
  const mvp = !tied && mvpId ? playersById.get(mvpId) : null;
  const [showMvp, setShowMvp] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShowMvp(true), 1800);
    return () => window.clearTimeout(t);
  }, []);

  // Share card — render the current standings to a 1080×1920 canvas and
  // write a PNG to the clipboard.
  const [shareState, setShareState] = useState<'idle' | 'copying' | 'copied' | 'fail'>('idle');
  const sceneRef = useRef<HTMLElement | null>(null);
  async function exportShareCard() {
    setShareState('copying');
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no ctx');
      // Background gradient.
      const grad = ctx.createLinearGradient(0, 0, 0, 1920);
      grad.addColorStop(0, '#1a1330');
      grad.addColorStop(1, '#0d0a16');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1080, 1920);
      // Winner ring.
      ctx.fillStyle = `hsl(${winnerHue} 70% 56%)`;
      ctx.beginPath();
      ctx.arc(540, 540, 220, 0, Math.PI * 2);
      ctx.fill();
      // Title.
      ctx.fillStyle = '#f8edd6';
      ctx.font = 'bold 88px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('Tile Tavern', 540, 220);
      ctx.font = 'italic 56px Georgia, serif';
      ctx.fillStyle = '#f5c14a';
      ctx.fillText(winner ? `${winner.name} wins` : 'Set complete', 540, 880);
      // Standings.
      ctx.font = '44px sans-serif';
      ctx.fillStyle = '#f8edd6';
      ctx.textAlign = 'left';
      let y = 1080;
      for (let i = 0; i < Math.min(4, standings.length); i++) {
        const s = standings[i]!;
        ctx.fillText(`${i + 1}.  ${s.name}`, 200, y);
        ctx.textAlign = 'right';
        ctx.fillText(`${s.points} pts`, 880, y);
        ctx.textAlign = 'left';
        y += 100;
      }
      // Watermark.
      ctx.fillStyle = '#b09a72';
      ctx.font = '32px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('tile-tavern · candle-lit', 540, 1820);

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('blob fail')), 'image/png');
      });
      if (typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      } else {
        // Fallback: download the file (Firefox/Safari without Clipboard API support).
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `tile-tavern-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
      setShareState('copied');
      window.setTimeout(() => setShareState('idle'), 2400);
    } catch {
      setShareState('fail');
      window.setTimeout(() => setShareState('idle'), 2400);
    }
  }

  return (
    <main ref={sceneRef as React.MutableRefObject<HTMLElement | null>} className="final-standings final-ceremony" style={{ ['--winner-hue' as never]: String(winnerHue) }}>
      <div className="ceremony-glow" aria-hidden="true" />
      <div className="ceremony-sparks" aria-hidden="true">
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="ceremony-spark"
            style={{
              left: `${8 + ((i * 19) % 84)}%`,
              animationDelay: `${(i % 9) * 0.12}s`,
              background: `hsl(${(winnerHue + i * 11) % 360} 72% 62%)`,
            }}
          />
        ))}
      </div>

      <header className="final-hero">
        <p className="final-kicker">Final Set Ceremony</p>
        <h1 className="display final-title">{tied ? 'The table ties' : winner ? `${winner.name} takes the table` : 'Set complete'}</h1>
        <p className="muted">{lobby.setRoundsPlayed} rounds played · {lobby.settings.eventMode === 'standard' ? 'Standard Night' : eventModeLabel(lobby.settings.eventMode)}</p>
        <div className="final-xp-bar">
          <XPBar charId={myCharId} boost {...(xpRefreshKey !== undefined ? { refreshKey: xpRefreshKey } : {})} />
        </div>
      </header>

      {winner && !tied && (
        <section className="winner-showcase">
          <div className="winner-medallion">
            {playersById.get(winner.charId) && <IconAvatar icon={playersById.get(winner.charId)!.icon} size={104} alt={winner.name} />}
          </div>
          <div>
            <span className="award-chip crown-chip">Set winner</span>
            <h2>{winner.points} table points</h2>
            <p className="muted">{margin > 0 ? `Won by ${margin}` : 'Won a tight table'}</p>
          </div>
        </section>
      )}
      {/* R2#63: in a tied final, server skipped awardSetWins so there's
          no real winner. Show a neutral tied banner instead of the
          showcase that crowns slot-order-first. */}
      {winner && tied && (
        <section className="winner-showcase tied-showcase" aria-label="tied final">
          <div className="winner-medallion">—</div>
          <div>
            <span className="award-chip">Tied — no winner</span>
            <h2>{winner.points} table points</h2>
            <p className="muted">
              {standings.filter((s) => s.points === winner.points).length} players tied at the top.
            </p>
          </div>
        </section>
      )}

      <ol className="podium ceremony-podium">
        {standings.slice(0, 4).map((s, i) => {
          const p = playersById.get(s.charId);
          // Stagger reveal: 4th appears first, 1st last.
          const placeIdx = Math.min(3, i);
          const stagger = (3 - placeIdx) * 0.45;
          return (
            <li
              key={s.charId}
              className={`podium-row place-${i + 1} podium-stagger`}
              style={{ animationDelay: `${stagger}s` } as React.CSSProperties}
            >
              <div className="podium-place">{i + 1}</div>
              {p && <IconAvatar icon={p.icon} size={48} alt={s.name} />}
              <div className="podium-name">{s.name}</div>
              <div className="podium-pts">{s.points}</div>
            </li>
          );
        })}
      </ol>

      {showMvp && mvp && (
        <section className="mvp-roll" aria-live="polite">
          <span className="mvp-kicker">MVP</span>
          <IconAvatar icon={mvp.icon} size={56} alt={mvp.name} />
          <strong>{mvp.name}</strong>
          <small>
            {(awardCount.get(mvp.charId) ?? 0) > 0
              ? `${awardCount.get(mvp.charId)} awards this set`
              : 'Most points across the table'}
          </small>
        </section>
      )}

      <section className="final-stats">
        <div className="final-stat">
          <span>Players</span>
          <strong>{standings.length}</strong>
        </div>
        <div className="final-stat">
          <span>Mode</span>
          <strong>{lobby.settings.mode}</strong>
        </div>
        <div className="final-stat">
          <span>Event</span>
          <strong>{eventModeLabel(lobby.settings.eventMode)}</strong>
        </div>
      </section>

      {awards.length > 0 && (
        <div className="final-awards ceremony-awards">
          {awards.map((a, i) => {
            const name = playersById.get(a.charId)?.name;
            return <span key={i} className="award-chip">{name ? `${name}: ${a.label}` : a.label}</span>;
          })}
        </div>
      )}

      {lastRoundXp && lastRoundXp.total > 0 && (
        <RoundXpSummary xp={lastRoundXp} variant="compact" />
      )}

      <div className="final-actions">
        {isHost && <button type="button" className="btn primary big" onClick={onNewSession}>New session</button>}
        {isHost && <button type="button" className="btn ghost big" onClick={onChangeSettings}>Change settings</button>}
        <button type="button" className="btn ghost big icon-btn" onClick={() => void exportShareCard()} disabled={shareState === 'copying'} aria-label="copy share card">
          <Share2 size={16} />
          {shareState === 'copying' ? 'Copying…'
            : shareState === 'copied'  ? 'Copied!'
            : shareState === 'fail'    ? 'Try again'
            : 'Share card'}
        </button>
        <button type="button" className="btn ghost big" onClick={onEnd}>End session</button>
      </div>
    </main>
  );
}

function eventModeLabel(mode: LobbyView['settings']['eventMode']): string {
  switch (mode) {
    case 'lightning': return 'Lightning Night';
    case 'comeback': return 'Comeback Night';
    case 'chaos': return 'Chaos Night';
    case 'cozy': return 'Cozy Night';
    case 'standard':
    default: return 'Standard Night';
  }
}
